<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import SharedContentBanner from '@/components/SharedContentBanner.vue'
import ShareLinkInvalidMessage from '@/components/ShareLinkInvalidMessage.vue'
import TermsOfUseFooter from '@/components/TermsOfUseFooter.vue'
import ProgressSpinner from 'primevue/progressspinner'
import { getSharedPresentLevelsStatement } from './api_v2'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { PRESENTLEVELS_EVENT_NAMES } from './event_names'
import type { SharedSubjectSummary } from './presentlevels_types'
import { wikiToPlainText } from '@/util/tokenize_wiki_text.ts'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { storeToRefs } from 'pinia'

const analytics = getAnalyticsTracker()
const route = useRoute()
const { profileId } = storeToRefs(useContentProfileStore())

const statementUuid = computed(() => route.params.uuid as string)
const statement = ref<SharedSubjectSummary | null>(null)
const ownerName = ref<string>('')
const loading = ref(true)
const error = ref(false)

const sanitizedStatementText = computed(() => {
  if (!statement.value?.summary.statement_text) return ''
  return wikiToPlainText(statement.value.summary.statement_text)
})

const fetchStatement = async () => {
  try {
    loading.value = true
    if (statementUuid.value) {
      const data = await getSharedPresentLevelsStatement(statementUuid.value)
      statement.value = data
      ownerName.value = data.owner || ''
    }
    analytics.sendEvent(PRESENTLEVELS_EVENT_NAMES.VIEWED_SHARED_STATEMENT, {
      folder_uuid: statement.value?.collection_uuid,
      plw_uuid: statementUuid.value,
      plw_is_library: true,
      student_uuid: statement.value?.collection_uuid, // support legacy attribute,
      content_profile_id: profileId.value,
    })
  } catch (err) {
    console.error('Error fetching shared present levels statement:', err)
    error.value = true
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchStatement()
})
</script>

<template>
  <div>
    <SharedContentBanner
      v-if="!loading && !error && statement"
      content-type="a present levels statement"
      :owner-name="ownerName"
      :last-updated="statement.summary.updated_at"
    />

    <div v-if="loading" class="flex justify-center items-center min-h-[400px]">
      <ProgressSpinner />
    </div>

    <div class="container mt-6" v-else-if="error">
      <ShareLinkInvalidMessage />
    </div>

    <div v-else-if="statement" class="container">
      <div class="flex justify-center mb-6">
        <h1 class="text-display-xl font-medium text-gray-900 mt-8">
          Present Levels Statement
        </h1>
      </div>

      <div class="flex flex-col items-center max-w-3xl mx-auto">
        <div class="w-full">
          <h2 class="text-lg text-gray-900 mb-4 font-bold">
            {{ statement.summary.subject_label }}
          </h2>
        </div>
        <div class="w-full border border-gray-600 rounded-xs p-6 bg-white fs-exclude">
          <div class="text-gray-900 p-1 statement-text" v-html="sanitizedStatementText"></div>
        </div>
      </div>
      <TermsOfUseFooter />
    </div>
  </div>
</template>

<style>
.statement-text {
  p { @apply p-1 min-h-6; }
}
</style>
